/*
  Product Story: viewport reveal and subtle parallax.
  Transform and opacity only. No wheel handlers or scroll locking.
*/

(function () {
  'use strict';

  const ROOT_SELECTOR = '[data-product-story]';
  const ITEM_SELECTOR = '[data-story-reveal]';
  const instances = new WeakMap();

  const prefersReducedMotion = () => {
    const theme = window.HouseOfThiraa;

    if (theme?.responsive?.matches) {
      return theme.responsive.matches('motionReduce');
    }

    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  const revealItem = (item) => {
    if (!item || item.classList.contains('is-revealed')) {
      return;
    }

    item.classList.add('is-revealed');

    const clearWillChange = () => {
      item.style.willChange = 'auto';
      item.removeEventListener('transitionend', onEnd);
    };

    const onEnd = (event) => {
      if (event.target !== item) {
        return;
      }

      if (event.propertyName !== 'opacity' && event.propertyName !== 'transform') {
        return;
      }

      clearWillChange();
    };

    item.addEventListener('transitionend', onEnd);
    window.setTimeout(clearWillChange, 1400);
  };

  const revealAll = (section) => {
    section.querySelectorAll(ITEM_SELECTOR).forEach((item) => {
      item.classList.add('is-revealed');
      item.style.willChange = 'auto';
    });
    section.classList.remove('is-reveal-armed');
  };

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const roleShift = (role, max) => {
    if (role === 'primary') {
      return max;
    }

    if (role === 'secondary') {
      return max * 0.72;
    }

    return max * 0.55;
  };

  const applyParallax = (section, maxMove) => {
    const rect = section.getBoundingClientRect();
    const viewport = window.innerHeight || 1;
    const progress = (viewport - rect.top) / (viewport + rect.height);
    const offset = clamp(progress, 0, 1) - 0.5;

    section.querySelectorAll('[data-story-parallax]').forEach((frame) => {
      const figure = frame.closest('[data-story-role]');
      const role = figure?.getAttribute('data-story-role') || 'image';
      const shift = roleShift(role, maxMove) * offset * -1;
      frame.style.transform = `translate3d(0, ${shift.toFixed(2)}px, 0)`;
    });
  };

  const initSection = (section) => {
    if (!(section instanceof Element) || instances.has(section)) {
      return;
    }

    const items = Array.from(section.querySelectorAll(ITEM_SELECTOR));
    const animate = section.getAttribute('data-story-animate') === 'true';
    const intensity = Number(section.getAttribute('data-story-intensity') || '40');
    const maxMove = clamp((Number.isFinite(intensity) ? intensity : 40) / 80 * 28, 8, 28);

    if (!items.length) {
      return;
    }

    if (!animate || prefersReducedMotion() || typeof IntersectionObserver !== 'function' || window.Shopify?.designMode) {
      revealAll(section);
      instances.set(section, { destroy() {} });
      return;
    }

    section.classList.add('is-reveal-armed');
    items.forEach((item) => {
      item.style.willChange = 'opacity, transform';
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          revealItem(entry.target);
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.08,
        rootMargin: '0px 0px -4% 0px'
      }
    );

    items.forEach((item) => observer.observe(item));

    let ticking = false;
    let inView = false;

    const onScroll = () => {
      if (!inView || ticking) {
        return;
      }

      ticking = true;
      window.requestAnimationFrame(() => {
        applyParallax(section, maxMove);
        ticking = false;
      });
    };

    const viewObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          inView = entry.isIntersecting;

          if (inView) {
            applyParallax(section, maxMove);
            window.addEventListener('scroll', onScroll, { passive: true });
          } else {
            window.removeEventListener('scroll', onScroll);
          }
        });
      },
      { threshold: 0, rootMargin: '12% 0px' }
    );

    viewObserver.observe(section);

    instances.set(section, {
      destroy() {
        observer.disconnect();
        viewObserver.disconnect();
        window.removeEventListener('scroll', onScroll);
        instances.delete(section);
      }
    });
  };

  const init = (root) => {
    const scope = root instanceof Element ? root : document;
    const sections = scope.matches?.(ROOT_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll(ROOT_SELECTOR));

    sections.forEach(initSection);
  };

  const destroy = (root) => {
    const scope = root instanceof Element ? root : document;
    const sections = scope.matches?.(ROOT_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll(ROOT_SELECTOR));

    sections.forEach((section) => {
      instances.get(section)?.destroy();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroy(event.target);
  });
})();
